import React, { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { getLocale, useIntl, history } from 'umi';
import { PagingQuery } from 'tea-component';
import { LANGUAGE_CN } from '@home/_config/config';
import { buildRobloxGenreLabelMap } from '@intelligence/v3/utils/robloxGenreLabel';
import TopchartTable from '@intelligence/components/TableComponents/TopchartTable';
import FirstRow from '@intelligence/components/TableComponents/components/FirstRow';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@intelligence/components/TableComponents/components/TableCheckbox';
import { getGameRankDetails } from '@intelligence/utils/tableUtils';
import { getRobloxTopChartsDrillDownHref } from '../../utils/robloxHubNavigation';

import type {
  TableSelectionType,
  TableSelectStatusType,
} from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import type { IMainGenre } from '@intelligence/types/common';
import type { IRobloxGameRankRow } from '../../types';

import { getRobloxGameRankMetricColumns, getRobloxGameRankSourceColumn } from './columns';
import './index.scss';

const CHECKBOX_SLOT_WIDTH = 36;
const CHECKBOX_RANK_GAP = 10;
const RANK_TEXT_WIDTH = 45;
const RANK_SELECT_COLUMN_WIDTH = CHECKBOX_SLOT_WIDTH + CHECKBOX_RANK_GAP + RANK_TEXT_WIDTH;
const GAMETITLE_COLUMN_WIDTH = 330;

interface IRobloxGameRankTableProps {
  loading: boolean;
  records: IRobloxGameRankRow[];
  total: number;
  pageIndex: number;
  pageSize: number;
  selectedSource: string;
  tableSelection: TableSelectionType[];
  tableSelectionAll: TableSelectionType[];
  rankOrder: 'asc' | 'desc';
  rankSortKey: string;
  onPagingChange: (paging: PagingQuery) => void;
  onCheckGame: (list: TableSelectionType[], status: TableSelectStatusType) => void;
  onSortChange: (sortItem: SortByItemType) => void;
  robloxGenreOptions?: IMainGenre[];
}

const RobloxGameRankTable: React.FC<IRobloxGameRankTableProps> = (props) => {
  const {
    loading,
    records,
    total,
    pageIndex,
    pageSize,
    selectedSource,
    tableSelection,
    tableSelectionAll,
    rankOrder,
    rankSortKey,
    onPagingChange,
    onCheckGame,
    onSortChange,
    robloxGenreOptions = [],
  } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isChinese = locale === LANGUAGE_CN;
  const robloxGenreLabelMap = useMemo(
    () => buildRobloxGenreLabelMap(robloxGenreOptions),
    [robloxGenreOptions],
  );
  const [selectList, setSelectList] = useState(tableSelection);
  const [showShadow, setShowShadow] = useState(false);

  useEffect(() => {
    setSelectList(tableSelection);
  }, [tableSelection]);

  useEffect(() => {
    if (loading) {
      return undefined;
    }

    const tableRoot = document.querySelector(
      '.dbi-roblox-game-rank-table-wrap .dbi-roblox-game-rank-table.tea-table',
    ) as HTMLElement | null;

    if (!tableRoot) {
      return undefined;
    }

    const scrollNodes = Array.from(
      tableRoot.querySelectorAll('.tea-table__body, .tea-table__header, .tea-table__box'),
    ).filter((node): node is HTMLElement => node instanceof HTMLElement);

    if (!scrollNodes.length) {
      return undefined;
    }

    const syncHeaderScroll = (scrollLeft: number): void => {
      scrollNodes.forEach((node) => {
        if (node.classList.contains('tea-table__header') && node.scrollLeft !== scrollLeft) {
          node.scrollLeft = scrollLeft;
        }
      });
    };

    const handleScroll = (event: Event): void => {
      const target = event.target as HTMLElement;
      const scrollLeft = target?.scrollLeft || 0;
      setShowShadow(scrollLeft > 0);
      syncHeaderScroll(scrollLeft);
    };

    const handleInitialScroll = (): void => {
      const maxScrollLeft = scrollNodes.reduce((max, node) => {
        return Math.max(max, node.scrollLeft || 0);
      }, 0);
      setShowShadow(maxScrollLeft > 0);
      syncHeaderScroll(maxScrollLeft);
    };

    handleInitialScroll();
    scrollNodes.forEach((node) => {
      node.addEventListener('scroll', handleScroll, { passive: true });
    });

    return () => {
      scrollNodes.forEach((node) => {
        node.removeEventListener('scroll', handleScroll);
      });
    };
  }, [loading, records]);

  const handleCheckChange = (
    list: TableSelectionType[],
    status: TableSelectStatusType,
  ): void => {
    setSelectList(list);
    onCheckGame(list, status);
  };

  const metricColumns = useMemo(
    () => getRobloxGameRankMetricColumns(formatMessage, {
      robloxGenreLabelMap,
      isChinese,
    }),
    [formatMessage, robloxGenreLabelMap, isChinese],
  );

  const sourceColumn = useMemo(
    () => getRobloxGameRankSourceColumn(formatMessage, selectedSource, {
      currentSortKey: rankSortKey,
      currentSortValue: rankOrder,
      onSortChange,
    }),
    [formatMessage, selectedSource, rankSortKey, rankOrder, onSortChange],
  );

  return (
    <div
      className={classnames('dbi-roblox-game-rank-table-wrap', {
        'table-sticky-two-shadow': showShadow,
      })}
    >
      <TopchartTable
        className="dbi-roblox-game-rank-table"
        loading={loading}
        recordKey="game_id"
        records={records}
        tableTotal={total}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPagingChange={onPagingChange}
        columns={[
          {
            key: 'rank_select',
            width: RANK_SELECT_COLUMN_WIDTH,
            fixed: 'left',
            header: () => (
              <div className="dbi-roblox-rank-col">
                <div className="dbi-roblox-rank-col__checkbox">
                  <TableCheckboxHeader
                    selectList={selectList}
                    tableSelectionAll={tableSelectionAll}
                    disabled={loading || records.length === 0}
                    onChange={handleCheckChange}
                  />
                </div>
                <div className="dbi-text-id">#</div>
              </div>
            ),
            render: (row: IRobloxGameRankRow) => (
              <div className="dbi-roblox-rank-col">
                <div className="dbi-roblox-rank-col__checkbox">
                  <TableCheckboxRender
                    selectList={selectList}
                    cvm={{ ...(row || {}), gameId: row?.game_id || '' } as TableSelectionType}
                    disabled={getGameRankDetails(row?.rank ?? undefined).isDisabled}
                    onChange={handleCheckChange}
                  />
                </div>
                <span className="dbi-text-id">
                  {getGameRankDetails(row?.rank ?? undefined)?.rankText}
                </span>
              </div>
            ),
          },
          {
            key: 'gametitle',
            fixed: 'left',
            header: (
              <div className="dbi-roblox-gametitle-col">
                {formatMessage({ id: 'dbi_top_gametitle' })}
              </div>
            ),
            width: GAMETITLE_COLUMN_WIDTH,
            render: (row: IRobloxGameRankRow) => {
              const gameId = String(row?.game_id || '');
              const gameTitle = row?.entity_name || gameId || '-';
              const drillDownHref = getRobloxTopChartsDrillDownHref(gameId, gameTitle);

              const handleDrillDown = (): void => {
                if (!gameId) {
                  return;
                }

                history.push(drillDownHref);
              };

              return (
                <div className="dbi-roblox-gametitle-col">
                  <FirstRow
                    className="dbi-roblox-gametitle-first-row"
                    cvm={{
                      img: row?.cover || '',
                      gametitle: gameTitle,
                      text: row?.publisher || '-',
                    }}
                    rihgtClickToJumpHref={drillDownHref}
                    onClick={handleDrillDown}
                  />
                </div>
              );
            },
          },
          sourceColumn,
          ...metricColumns,
        ]}
      />
    </div>
  );
};

export default RobloxGameRankTable;
